<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import DriverModel from '@models/Driver';
import ParamModel from '@models/Param';
import Multiselect from 'vue-multiselect';
import map from 'lodash/map';
import HeaderComponent from '@components/HeaderComponent';
import { DateTime } from 'luxon';
import utility from '@mixins/utility';

export default {
  page() {
    return {
      title: this.$t('titles.drivers'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    Multiselect,
    HeaderComponent,
  },
  mixins: [utility],
  data() {
    return {
      sortBy: 'last_name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'selected', sortable: false, label: this.$t('table_labels.select') },
        { key: 'id', sortable: true },
        { key: 'last_name', label: this.$t('table_labels.last_name'), sortable: true },
        { key: 'first_name', label: this.$t('table_labels.first_name'), sortable: true },
        { key: 'email', label: this.$t('table_labels.email'), sortable: true },
        { key: 'mobile_number', label: this.$t('table_labels.mobile_number'), sortable: true },
        { key: 'active', label: this.$t('table_labels.active'), sortable: true },
        {
          key: 'last_service_date',
          label: this.$t('table_labels.last_job'),
          sortable: false,
          formatter: (value) => (value == null ? '' : DateTime.fromSQL(value).toFormat('dd.MM.yyyy')),
        },
        { key: 'verified', label: this.$t('table_labels.verified'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
        locations: null,
      },
      locations: [],
      focused: false,
      selectedItems: [],
      selectAll: false,
      drivers: [],
      selectionOptionSelected: null,
      selectionOptions: [
        { text: this.$t('fields.select_option'), value: null },
        { text: this.$t('fields.drivers_export'), value: 0, disabled: this.selectAll },
      ],      
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$nextTick(() => {
          this.$refs.asyncTable.refresh();
        });
      },
      immediate: false,
      deep: true,
    },
  },
  mounted() {
    ParamModel.params({
      select_many: ['locations'].join(','),
    })
      .first()
      .then((response) => {
        this.locations = response.locations;
      });
  },
  methods: {
    customLocationName({ name }) {
      return name;
    },
    restore(id) {
      if (confirm(this.$t('alerts.restore_entity', { entity: this.$t('entities.driver') }))) {
        this.$wait.start('driver processing');
        new DriverModel({
          id: `${id}/restore`,
        })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('driver processing');
          })
          .catch(() => {
            this.$wait.end('driver processing');
          });
      }
    },
    itemsProvider(ctx) {
      return DriverModel.orderBy(this.sortDir + ctx.sortBy)
        .params({
          locations: map(this.filters.locations, 'id'),
        })
        .where('search', this.filters.search)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          this.drivers = response.data;
          return items || [];
        })
        .catch(() => []);
    },
    verifyUser(id) {
      this.$wait.start(`verification ${id}`);
      new DriverModel({ id: `make-verified/${id}` })
        .save()
        .then(() => {
          this.$refs.asyncTable.refresh();
          this.$wait.end(`verification ${id}`);
        })
        .catch(() => {
          this.$wait.end(`verification ${id}`);
        });
    },
    async toggleActive(id) {
      this.$wait.start('driver processing');
      new DriverModel({ id: `${id}/toggle-active` })
        .save()
        .then(() => {
          this.$refs.asyncTable.refresh();
          this.$wait.end('driver processing');
        })
        .catch(() => {
          this.$wait.end('driver processing');
        });
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.driver') }))) {
        try {
          this.$wait.start('driver processing');
          const driver = new DriverModel({ id });
          await driver.delete();
          this.$refs.asyncTable.refresh();
        } catch (error) {
        } finally {
          this.$wait.end('driver processing');
        }
      }
    },
    goToEditDriver(driverId) {
      const routeData = this.$router.resolve({ name: 'drivers.edit', params: { id: driverId } });
      window.open(routeData.href, '_blank');
    },
    downloadVCard(id, firstName, lastName) {
      this.$wait.start('global loading')
      this.$http
        .get(`admin/drivers/${id}/vcard`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end('global loading');
          const blob = new Blob([response.data], { type: 'text/x-vcard' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `${lastName} ${firstName}.vcf`;
          link.click();
        })
        .catch(() => this.$wait.end('global loading'));
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),

    clearSelected() {
      this.selectedItems = [];
      this.selectAll = false;
    },

    selectAllDrivers() {
      if (this.selectAll) {
        this.$wait.start('select-all-drivers');
        const params = {
          locations: map(this.filters.locations, 'id'),
        };
        
        if (this.filters.search) params['filter[search]'] = this.filters.search;

        this.$http
          .get('admin/drivers/all-ids', { params })
          .then((response) => {
            this.selectedItems = response.data;
          })
          .catch(() => {
            this.selectAll = false;
          })
          .finally(() => {
            this.$wait.end('select-all-drivers');
          });
      } else {
        this.selectedItems = [];
      }
    },
    exportPdf() {
      this.$wait.start('global loading');
      this.$http
        .get('admin/drivers/export', { params: { ids: this.selectedItems }, responseType: 'blob' })
        .then((response) => {
          this.$wait.end('global loading');
          this.clearSelected();
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Selected drivers export`;
          link.click();
        })
        .catch(() => {
          this.$wait.end('global loading');
        }).finally(() => {
          this.$wait.end('global loading');
        });
    },    
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.drivers')"
          :button-text="$t('titles.create_driver')"
          @button-pressed="$router.push({ name: 'drivers.create' })"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4" class="mb-3 mb-md-0">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [$t('search.entities.name'), $t('search.entities.email')].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="12" lg="4" class="mb-3">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.locations') }}</span>
          </template>
        </HeaderComponent>
        <b-input-group>
          <div class="multiselect-container kks-multiselect-container-index form-control form-control-sm p-0" :class="{ focused: focused }">
            <Multiselect
              v-model="filters.locations"
              :options="locations"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :placeholder="$t('placeholders.locations_multiselect')"
              :label="$t('fields.locations')"
              :custom-label="customLocationName"
              track-by="id"
              @open="focused = true"
              @close="focused = false"
              >&nbsp;</Multiselect
            >
          </div>
          <b-input-group-append>
            <b-btn
              variant="secondary"
              size="sm"
              class="text-white border-gray"
              :disabled="filters.locations === null || !filters.locations.length"
              @click="filters.locations = []"
              >{{ $t('actions.clear') }}</b-btn
            >
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="12" lg="4" class="mb-3">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('titles.selection_options') }}</span
            >&nbsp;
          </template>
        </HeaderComponent>
        <b-input-group size="sm">
          <b-form-select v-model="selectionOptionSelected" :options="selectionOptions" @change="clearSelected"></b-form-select>
          <b-input-group-append>
            <b-btn
              variant="secondary"
              size="sm"
              class="text-white border-gray"
              :disabled="selectionOptionSelected === null"
              @click="selectionOptionSelected = null;clearSelected()"
              >{{ $t('actions.clear') }}</b-btn
            >
          </b-input-group-append>
        </b-input-group>
      </b-col>      
    </b-row>
    <b-row v-if="selectionOptionSelected === 0">
      <b-col sm="12" md="12" lg="4" class="mb-3">
        <b-button
          block
          :disabled="selectedItems.length === 0"
          class="mb-2"
          size="md"
          variant="secondary"
          @click="exportPdf"
          >{{ $t('titles.export_drivers') }}</b-button
        >
      </b-col>      
    </b-row>
    <div id="reports-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('driver processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <template #head(selected)="data">
          <span>{{ data.label }}</span>
          <span>
            <FontAwesomeIcon v-if="$wait.is('select-all-drivers')" icon="spinner" spin />
            <b-form-checkbox v-else id="checkboxSelectAll" v-model="selectAll" @change="selectAllDrivers"> </b-form-checkbox>
          </span>
        </template>
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(selected)="data">
          <b-form-checkbox
            :id="'checkbox' + data.index"
            v-model="selectedItems"
            :value="data.item.id"
          >
          </b-form-checkbox>
        </template>        
        <template v-slot:cell(verified)="row">
          <div class="no-wrap">
            <FontAwesomeIcon
              v-if="!$wait.is(`verification ${row.item.id}`)"
              class="mr-2 pointer"
              :class="row.item.verification_token === null ? 'text-success' : 'text-grey'"
              :icon="['fas', row.item.verification_token != null ? 'user-times' : 'user-check']"
              @click="verifyUser(row.item.id)"
            />
            <FontAwesomeIcon v-else :icon="['fas', 'spinner']" pulse />
          </div>
        </template>
        <template v-slot:cell(active)="row">
          <b-form-checkbox v-model="row.item.active" name="check-active-button" switch :value="1" :unchecked-value="0" @change="toggleActive(row.item.id)">
          </b-form-checkbox>
        </template>
        <template v-slot:cell(actions)="row">
          <div v-if="row.item.deleted_at == null">
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="downloadVCard(row.item.id, row.item.first_name, row.item.last_name)">
              <FontAwesomeIcon :icon="['fas', 'address-card']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="goToEditDriver(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
          <div v-else>
            <b-btn class="mr-2" size="sm" variant="outline-success" @click="restore(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'trash-restore']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <div class="d-flex justify-content-start align-items-left mt-2">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
          <div class="text-secondary d-flex align-items-center justify-content-center mb-3 px-4">
            {{  $t('infos.showing_results', {
              start: (currentPage - 1) * perPage + 1,
              end: Math.min(currentPage * perPage, totalRows),
              total: totalRows
            }) }}
          </div>
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
